import * as React from 'react';
import isNil from 'lodash/isNil';

import PageSection from 'components/page/page-section/PageSection';
import TabsSection from 'components/tabs/tabs-section/TabsSection';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import CcmCampaignReportingMetrics from './metrics/CcmCampaignReportingMetrics';
import CcmCampaignReportingTable from './table/CcmCampaignReportingTable';

import { __t, applyTranslations } from 'utils/language';
import {
  getCountriesDropdownItems,
  getStatusLabelTextKey
} from 'modules/campaigns/components/ccm/utils/ccm-campaign';
import {
  CCM_REPORTING_TYPES
} from './utils/ccm-campaign-reporting';

import './ccm-campaign-reporting.scss';

applyTranslations(require('./ccm-campaign-reporting.i18n.json'));

import type {
  Campaign,
  LocationWindow,
  MixedSelectDropdownItem
} from 'types';

import type {
  CcmCampaignReportingOutfitPerformance
} from 'modules/campaigns/components/ccm/types/CcmCampaignReporting.type';

type Props = {
  campaign: Campaign,
  outfitPerformance: CcmCampaignReportingOutfitPerformance | null,
  outfitPerformanceCountries: Array<MixedSelectDropdownItem>,
  location: LocationWindow,
  dispatchFetchOutfitPerformance: (
    campaignId: number,
    countries: Array<MixedSelectDropdownItem>
  ) => Promise<void>,
  dispatchUpdateOutfitPerformanceCountries: (
    outfitPerformanceCountries: Array<MixedSelectDropdownItem>
  ) => Promise<void>
};

const MSG_KEY = 'ccm_campaign_reporting';
const DEFAULT_SORTING_OPTIONS = {
  sortingKey: 'outfitsRevenue',
  ascending: false
};

const CcmCampaignReporting: React.FC<Props> = ({
  campaign,
  outfitPerformance,
  outfitPerformanceCountries,
  location,
  dispatchFetchOutfitPerformance,
  dispatchUpdateOutfitPerformanceCountries
}): JSX.Element => {
  const [activeTab, setActiveTab] = React.useState(CCM_REPORTING_TYPES[0]);
  const [page, setPage] = React.useState(1);
  const [sortingOptions, setSortingOptions] = React.useState(DEFAULT_SORTING_OPTIONS);
  const [expandedRows, setExpandedRows] = React.useState<Array<number>>([]);

  const shouldHaveData = getStatusLabelTextKey(campaign) === 'live'
    || getStatusLabelTextKey(campaign) === 'ended';

  const handleTabSwitch = (
    currentTab?: string | null
  ) => {
    setPage(1);
    setSortingOptions(DEFAULT_SORTING_OPTIONS);
    setExpandedRows([]);
    setActiveTab(currentTab ?? activeTab);
  };

  const handlePageChange = (
    updatedPage: number
  ) => {
    setPage(updatedPage);
    setSortingOptions(DEFAULT_SORTING_OPTIONS);
    setExpandedRows([]);
  };

  const handleSetExpandedRows = (
    rowId: number | null
  ): void => {
    if (isNil(rowId)) {
      setExpandedRows([]);
      return;
    }

    setExpandedRows((prevExpandedRows) => {
      if (prevExpandedRows.includes(rowId)) {
        return prevExpandedRows.filter(id => id !== rowId);
      }

      return [...prevExpandedRows, rowId];
    });
  };

  const handleCountriesChange = (
    items: Array<MixedSelectDropdownItem>
  ): void => {
    handleSetExpandedRows(null);
    dispatchUpdateOutfitPerformanceCountries(items);
    dispatchFetchOutfitPerformance(campaign.id, items.filter(item => item.isSelected));
  };

  const renderMetricsBar = (): React.ReactNode => (
    <div className="ccm-campaign-reporting__metrics">
      <CcmCampaignReportingMetrics
        summary={outfitPerformance?.summary}
        shouldHaveData={shouldHaveData}
      />
    </div>
  );

  const renderFilter = (): React.ReactNode => (
    <div className="ccm-campaign-reporting__filter">
      <div className="ccm-campaign-reporting__filter-label">
        {__t(`${MSG_KEY}.labels.filter_by_country`)}
      </div>
      <div className="ccm-campaign-reporting__filter-countries">
        <MixedSelectDropdown
          name="ccm-campaign-reporting__filter-countries"
          items={outfitPerformanceCountries}
          placeholder={__t(`${MSG_KEY}.countries.placeholder`)}
          withMulti
          withRemovable
          withSortable
          withCheckboxes
          withSelectAll
          onItemsToggle={handleCountriesChange}
          onItemsRemove={handleCountriesChange}
          onItemsRemoveAll={handleCountriesChange}
          onItemsToggleAll={handleCountriesChange}
        />
      </div>
    </div>
  );

  const renderTabInfo = (): React.ReactNode => {
    const data = activeTab === 'country'
      ? outfitPerformance?.performanceByCountry ?? []
      : outfitPerformance?.performanceByOutfit ?? [];

    if (activeTab === 'country' || activeTab === 'outfit') {
      return (
        <CcmCampaignReportingTable
          type={activeTab}
          data={data}
          page={page}
          sortingOptions={sortingOptions}
          expandedRows={expandedRows}
          location={location}
          onSetSortingOptions={setSortingOptions}
          onSetExpandedRows={handleSetExpandedRows}
          onSetPage={handlePageChange}
        />
      );
    }

    return null;
  };

  const renderAdditionalInfo = (): React.ReactNode => (
    <React.Fragment>
      <div className="ccm-campaign-reporting__tabs">
        <TabsSection
          withShadow
          clickableActiveTab
          items={CCM_REPORTING_TYPES.map(type => ({
            identifier: type,
            label: __t(`${MSG_KEY}.tabs.${type}`)
          }))}
          activeTab={activeTab}
          onClick={handleTabSwitch}
        />
      </div>
      {activeTab === 'outfit' && renderFilter()}
      {renderTabInfo()}
    </React.Fragment>
  );

  React.useEffect(() => {
    const countries = getCountriesDropdownItems(campaign.countries);
    dispatchUpdateOutfitPerformanceCountries(countries);
  }, [campaign.id]);

  return (
    <div className="ccm-campaign-reporting">
      <PageSection
        padding="none"
        style="none"
        title={__t(`${MSG_KEY}.title`)}
      >
        <div className="ccm-campaign-reporting__contents">
          {renderMetricsBar()}
          {shouldHaveData && renderAdditionalInfo()}
        </div>
      </PageSection>
    </div>
  );
};

export default CcmCampaignReporting;
